class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

getSearchResults(searchTerm) {
  const queryKey = searchTerm.replaceAll(" ", "-").toLowerCase();

  this.setLiveRegionLoadingState();

  if (this.cachedResults[queryKey]) {
    this.renderSearchResults(this.cachedResults[queryKey]);
    return;
  }

  if (this.shouldUseShopify()) {
    this.getShopifyResults(searchTerm, queryKey);
    return;
  }

  const limit = Number(this.dataset.limit) || 10;
  const { liteClient } = window["algoliasearch/lite"];

  const searchClient = liteClient(
    "WLYBF4QWV3",
    "4c6513e0016e763da92b5a7d614a5f4a"
  );

  searchClient.search([
    {
      indexName: "shopify_products",
      params: {
        query: searchTerm,
        hitsPerPage: limit,
        attributesToRetrieve: [
          "title",
          "handle",
          "url",
          "image",
          "featured_image",
          "vendor",
          "price",
          "sku",
          "variants",
          "meta",
        ]
      }
    }
  ])
    .then(({ results }) => {
      const data = results[0];
      const resultsMarkup = this.buildAlgoliaResults(data.hits || [], searchTerm);

      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    })
    .catch((error) => {
      this.close();
      console.error(error);
    });
}

shouldUseShopify() {
  return (
    window.LWSAlgoliaSettings?.forceShopify === true ||
    window.LWSAlgoliaSettings?.b2bCustomer === true ||
    new URLSearchParams(window.location.search).has("force_shopify")
  );
}

async getShopifyResults(searchTerm, queryKey) {
  try {
    const limit = Number(this.dataset.limit) || 10;
    const languageRoot = window.LWSAlgoliaSettings?.languageRootUrl || "";
    const base = languageRoot === "/" ? "" : languageRoot;
    const url = new URL(`${base}/search`, window.location.origin);
    url.searchParams.set("view", "suggest-json");
    url.searchParams.set("type", "product");
    url.searchParams.set("q", searchTerm);

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`Shopify suggest fetch failed: ${response.status}`);
    }

    const data = await response.json();
    const hits = (data.products || []).slice(0, limit);

    const resultsMarkup = this.buildAlgoliaResults(hits, searchTerm);
    this.cachedResults[queryKey] = resultsMarkup;
    this.renderSearchResults(resultsMarkup);
  } catch (error) {
    this.close();
    console.error(error);
  }
}

buildAlgoliaResults(hits, searchTerm) {
  const textProducts = this.dataset.textProducts || "Products";
  const textVendor = this.dataset.textVendor || "Vendor";
  const textSearchFor = this.dataset.textSearchFor || 'Search for “[term]”';
  const textNoResults = this.dataset.textNoResults || "No results for [term]";
  const textResultsCount = this.dataset.textResultsCount || "[count] results for [term]";
  const textCatalogNumber = this.dataset.textCatalogNumber || "Catalog Number";
  const textModelNumber = this.dataset.textModelNumber || "Parts Number";
  const productsMarkup = hits.map((product, index) => {
    const title = product.title || "";
    const handle = product.handle || "";
    const url = product.url || `/products/${handle}`;
    const image =
  product.image ||
  product.featured_image ||
  window.LWSAlgoliaSearchSettings?.placeholderImage ||
  "";
    const vendor = product.vendor || "";
    const price = product.price;
    const sku = product.sku || product.variants?.[0]?.sku || "";

    const catalogNumber =
      product.meta?.catalog_number ||
      product.meta?.custom?.catalog_number ||
      product["meta.catalog_number"] ||
      "";

    const modelNumber =
      product.meta?.model_number ||
      product.meta?.custom?.model_number ||
      product["meta.model_number"] ||
      "";

    return `
      <li
        id="predictive-search-option-product-${index + 1}"
        class="predictive-search-list-item predictive-search-list-item-product"
        role="option"
        aria-selected="false"
      >
        <a
          href="${this.escapeHtml(url)}"
          class="predictive-search-item predictive-search-item-link"
          tabindex="-1"
          title="${this.escapeHtml(title)}"
        >
          <div class="predictive-search-item-image-wrap">
            ${
              image
                ? `
                  <img
                    src="${this.escapeHtml(image)}"
                    class="predictive-search-item-image"
                    width="140"
                    loading="lazy"
                    alt="${this.escapeHtml(title)}"
                  >
                `
                : ""
            }
          </div>

          <div class="predictive-search-item-content">
            ${
              vendor
                ? `
                  <span class="visually-hidden">${this.escapeHtml(textVendor)}</span>
                  <div class="predictive-search-item-vendor">
                    ${this.escapeHtml(vendor)}
                  </div>
                `
                : ""
            }

            <h3 class="predictive-search-item-heading">
              ${this.escapeHtml(title)}
            </h3>

            ${sku ? `<div>${this.escapeHtml(sku)}</div>` : ""}
            ${catalogNumber ? `<div class="predictive-search-item-metafield">${this.escapeHtml(textCatalogNumber)}: ${this.escapeHtml(catalogNumber)}</div>` : ""}
            ${modelNumber ? `<div class="predictive-search-item-metafield">${this.escapeHtml(textModelNumber)}: ${this.escapeHtml(modelNumber)}</div>` : ""}
            ${price ? `<div class="price">${this.formatMoney(price)}</div>` : ""}
          </div>
        </a>
      </li>
    `;
  }).join("");

  return `
    <div id="predictive-search-results">
      <div
        id="predictive-search-results-list"
        class="predictive-search-results-list ${hits.length === 0 ? "predictive-search-results-list-empty" : ""}"
        role="listbox"
        aria-labelledby="predictive-search-products"
      >
        <span class="spinner-xs"></span>

        <div class="predictive-search-results-group-wrapper">
          <div class="predictive-search-results-group" style="width:100%;">
            ${
              hits.length > 0
                ? `
                  <div class="predictive-search-results-group-section">
                    <h3 id="predictive-search-products" class="predictive-search-heading h5">
                      ${this.escapeHtml(textProducts)}
                    </h3>

                    <ul
                      id="predictive-search-results-item-product-list"
                      class="predictive-search-results-item-list"
                    >
                      ${productsMarkup}
                    </ul>
                  </div>
                `
                : ""
            }
          </div>
        </div>
      </div>

      <div
        id="predictive-search-option-search-keywords"
        class="predictive-search-list-item-term"
        role="option"
      >
        <button
          class="predictive-search-item-button predictive-search-item-term typograph-body"
          tabindex="-1"
        >
          ${this.escapeHtml(textSearchFor).replace("[term]", this.escapeHtml(searchTerm))}
          <span class="angle angle-right">
            <span class="angle-icon"></span>
          </span>
        </button>
      </div>

      ${
        hits.length === 0
          ? `
            <div class="predictive-search-loading-state" aria-hidden="true">
              <span class="spinner-xs"></span>
            </div>
          `
          : ""
      }
    </div>

    <span class="hidden" data-predictive-search-live-region-count-value>
      ${
        hits.length === 0
          ? this.escapeHtml(textNoResults).replace("[term]", this.escapeHtml(searchTerm))
          : this.escapeHtml(textResultsCount)
              .replace("[term]", this.escapeHtml(searchTerm))
              .replace("[count]", hits.length)
      }
    </span>
  `;
}

formatMoney(price) {
  const number = Number(price);

  if (Number.isNaN(number)) {
    return this.escapeHtml(price);
  }

  return new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD"
  }).format(number / 100);
}

escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
